import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import type { RealSpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import {
  createUuid, type Uuid,
} from '~/_shared/utils/createUuid';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import { isString } from '~/_shared/utils/typeGuards';
import type { RealSpreadsheetVersionId } from '~/spreadsheet/spreadsheet.repository';
import { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import type { BoundaryStatsResponseItem } from './stats.repository';
import type {
  BoundaryStatItemDemographic,
  BoundaryStatItemSpreadsheetColumn, StatItem,
} from './stats.state';

export const createEmptyBoundaryStatItemSpreadsheetColumn = ({
  boundaryGroupId,
  column,
}: {
  boundaryGroupId: BoundaryStatItemSpreadsheetColumn['boundaryGroupId'];
  column: BoundaryStatItemSpreadsheetColumn['column'];
}): BoundaryStatItemSpreadsheetColumn => ({
  id: createUuid(),
  boundaryGroupId,
  column,
  type: ChartMetricType.SpreadsheetColumn,
  data: {
    status: FetchableResourceStatus.Requested,
  },
});

export const createEmptyBoundaryStatItemDemographic = ({
  boundaryGroupId,
  demographicId,
}: {
  boundaryGroupId: BoundaryStatItemDemographic['boundaryGroupId'];
  demographicId: BoundaryStatItemDemographic['demographicId'];
}): BoundaryStatItemDemographic => ({
  id: createUuid(),
  boundaryGroupId,
  demographicId,
  type: ChartMetricType.Demographic,
  data: {
    status: FetchableResourceStatus.Requested,
  },
});

export const createLoadedStatItem = (
  id: Uuid,
  serverItem: BoundaryStatsResponseItem,
): StatItem | null => {
  if (serverItem.type === ChartMetricType.SpreadsheetColumn
    && serverItem.spreadsheet_version
    && serverItem.spreadsheet_id
    && isString(serverItem.metric) // metric should be a string (ColumnId) for SpreadsheetColumn type
  ) {
    return {
      id,
      boundaryGroupId: serverItem.boundary_group_id,
      column: { spreadsheetId: serverItem.spreadsheet_id, columnId: serverItem.metric },
      type: ChartMetricType.SpreadsheetColumn,
      data: {
        status: FetchableResourceStatus.Loaded,
        value: {
          serverId: serverItem.id,
          computedAt: new Date(serverItem.computed_at),
          ...(serverItem.data ? {
            data: {
              median: serverItem.data.median,
              avg: serverItem.data.avg,
              values: serverItem.data.values || {},
            },
          } : {}),
          extra: {
            boundaryGroupEtag: serverItem.boundary_group_timestamp,
            spreadsheetVersions: stringObjectEntries(serverItem.spreadsheet_version).reduce((acc, [realSpreadsheetIdString, versionId]) => {
              acc.set(+realSpreadsheetIdString, versionId);
              return acc;
            }, new Map<RealSpreadsheetId, RealSpreadsheetVersionId>()),
          },
          status: serverItem.status,
        },
      },
    };
  }

  if (serverItem.type === ChartMetricType.Demographic) {

    return {
      id,
      boundaryGroupId: serverItem.boundary_group_id,
      demographicId: serverItem.metric,
      type: ChartMetricType.Demographic,
      data: {
        status: FetchableResourceStatus.Loaded,
        value: {
          serverId: serverItem.id,
          computedAt: new Date(serverItem.computed_at),
          ...(serverItem.data ? {
            data: {
              median: serverItem.data.median,
              avg: serverItem.data.avg,
              values: serverItem.data.values || {},
            },
          } : {}),
          extra: {
            boundaryGroupEtag: serverItem.boundary_group_timestamp,
          },
          status: serverItem.status,
        },
      },
    };
  }

  return null;
};
